import React, { KeyboardEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DedicatedServerCreateOptions, DedicatedServerHardware } from '../../../api/bareMetalTypes'
import { Button } from '../../../components'
import { formatHardwareFrequency } from '../../../utils/hardware-units'
import { diskTypeLabel, formatCapacity } from '../list/format'
import Price, { PricePeriod } from './price'
import {
  Aside, Card, CardHead, CardTitle, DiskChip, DiskList, ErrorLine, Faint, Radio, Small, StockColumns,
  Segment, SegmentGroup, StockFooter, StockHead, StockName, StockPrice, StockRow,
} from './ui'

export type StockServer = Pick<DedicatedServerCreateOptions['servers'][number], 'id' | 'hardware' | 'price'> & { name: string }

const diskFilters = ['all', 'NVMe', 'SSD', 'HDD'] as const
const ramFilters = [0, 64, 128] as const
const GB = 1024 ** 3

const unitBytes: Record<string, number> = { b: 1, k: 1024, kb: 1024, m: 1024 ** 2, mb: 1024 ** 2, g: GB, gb: GB, t: 1024 ** 4, tb: 1024 ** 4 }
const ramGb = (ram: DedicatedServerHardware['ram']) => (ram.value * (unitBytes[(ram.value_type || '').toLowerCase()] ?? GB)) / GB

export const diskSummary = (disk: DedicatedServerHardware['disk']) => {
  const details = disk.details?.length ? disk.details : []
  const groups = new Map<string, { count: number; type: string; label: string }>()
  details.forEach(item => {
    const type = diskTypeLabel(item.disk_type)
    const size = formatCapacity(item.value, item.value_type)
    const key = `${size}|${type}`
    const group = groups.get(key) || { count: 0, type, label: `${size} ${type}`.trim() }
    group.count += 1
    groups.set(key, group)
  })
  return {
    groups: Array.from(groups.values()).map(group => ({ ...group, text: `${group.count} × ${group.label}` })),
    total: details.length > 1 ? formatCapacity(disk.value, disk.value_type) : '',
  }
}

export const cpuSummary = (cpu: DedicatedServerHardware['cpu'], cores: string) => ({
  title: `${cpu.count} × ${cpu.model.replace(/^Intel\s+/i, '')}`,
  sub: [cpu.cores ? `${cpu.cores * (cpu.count || 1)} ${cores}` : '', cpu.speed ? formatHardwareFrequency(cpu.speed, cpu.speed_type || 'MHz') : ''].filter(Boolean).join(' · '),
})

type Props = {
  servers: StockServer[]
  value: string
  onChange: (id: string) => void
  onPreorder: () => void
  error?: string
  pricePeriod?: PricePeriod
  pricesLoading?: boolean
}

export default function StockServers({ servers, value, onChange, onPreorder, error, pricePeriod = 'day', pricesLoading }: Props) {
  const [t] = useTranslation()
  const [disk, setDisk] = useState<typeof diskFilters[number]>('all')
  const [ram, setRam] = useState<typeof ramFilters[number]>(0)
  const visible = servers.filter(server =>
    (disk === 'all' || server.hardware.disk.details?.some(item => diskTypeLabel(item.disk_type) === disk)) &&
    ramGb(server.hardware.ram) >= ram)

  return <Card $flush $error={Boolean(error)} data-validation-field="server_id" data-validation-error={error ? true : undefined}
    tabIndex={error ? -1 : undefined} aria-labelledby="dedicated-stock-title">
    <StockHead>
      <CardHead style={{ marginRight: 'auto' }}>
        <CardTitle id="dedicated-stock-title">{t('DEDICATED_SERVER')} <Aside>· {t('DEDICATED_AVAILABLE_COUNT', { count: visible.length })}</Aside></CardTitle>
        {error && <ErrorLine role="alert">{error}</ErrorLine>}
      </CardHead>
      <RadioSegments label={String(t('DEDICATED_FILTER_DISK'))} value={disk} onChange={setDisk}
        options={diskFilters.map(item => ({ value: item, label: item === 'all' ? String(t('DEDICATED_ANY_DISK')) : item }))} />
      <RadioSegments label={String(t('DEDICATED_FILTER_RAM'))} value={ram} onChange={setRam}
        options={ramFilters.map(item => ({ value: item, label: item === 0 ? String(t('DEDICATED_ANY_RAM')) : `≥ ${item} GB` }))} />
    </StockHead>
    <StockColumns aria-hidden="true">
      <span />
      <span>{t('NAME')}</span>
      <span>{t('DEDICATED_PROCESSOR')}</span>
      <span>RAM</span>
      <span>{t('STORAGE')}</span>
      <span>{t('PRICE')}</span>
    </StockColumns>
    <div role="radiogroup" aria-labelledby="dedicated-stock-title">
      {visible.map(server => {
        const cpu = cpuSummary(server.hardware.cpu, String(t('DEDICATED_CORES')))
        const disks = diskSummary(server.hardware.disk)
        const selected = server.id === value
        return <StockRow key={server.id} type="button" role="radio" aria-checked={selected} $active={selected}
          aria-label={server.name} aria-describedby={`server-price-${server.id}`} onClick={() => onChange(server.id)}>
          <span><Radio $checked={selected} aria-hidden="true" /></span>
          <StockName>{server.name}</StockName>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span>{cpu.title}</span>
            {cpu.sub && <Small>{cpu.sub}</Small>}
          </span>
          <span>{formatCapacity(server.hardware.ram.value, server.hardware.ram.value_type, true)}</span>
          <DiskList>
            {disks.groups.map(group => <DiskChip key={group.text} $accent={group.type === 'NVMe'}>{group.text}</DiskChip>)}
            {disks.total && <Faint>= {disks.total}</Faint>}
          </DiskList>
          <StockPrice id={`server-price-${server.id}`}><Price price={server.price} period={pricePeriod} loading={pricesLoading} /></StockPrice>
        </StockRow>
      })}
      {!visible.length && <StockFooter style={{ borderRadius: 0 }}><span>{t('DEDICATED_NO_FILTER_MATCH')}</span></StockFooter>}
    </div>
    <StockFooter>
      <span>{t('DEDICATED_NO_MATCH')}</span>
      <Button variant="stroke" size="mid" title={t('DEDICATED_BUILD_ON_PREORDER')} onClick={onPreorder} />
    </StockFooter>
  </Card>
}

type SegmentsProps<T extends string | number> = {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}

// Radio group with roving focus: Tab enters the checked option, arrows move between options.
function RadioSegments<T extends string | number>({ label, value, options, onChange }: SegmentsProps<T>) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0
    if (!step) return
    event.preventDefault()
    const index = options.findIndex(option => option.value === value)
    const next = (index + step + options.length) % options.length
    onChange(options[next].value)
    const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')
    buttons[next]?.focus()
  }
  return <SegmentGroup role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
    {options.map(option => {
      const checked = option.value === value
      return <Segment key={String(option.value)} type="button" role="radio" aria-checked={checked}
        tabIndex={checked ? 0 : -1} $active={checked} onClick={() => onChange(option.value)}>
        {option.label}
      </Segment>
    })}
  </SegmentGroup>
}
